import {ScreenComponent} from "./ScreenComponent.js";
import {cloneInstance} from "../GWDService.js";
import {div, fitText, setText} from '@megaphonenpm/mputils/src/DOM.js';
import {cascadingGet, isNil, notNil} from '@megaphonenpm/mputils/src/utils.js';
import {play} from "../Animation.js";

/**
 * A label that displays the current average vote above Guess Gauge's survey bar and
 * to the right of Mood Meter's dial.
 */
export class InstrumentValueLabelComponent extends ScreenComponent {
  constructor (themeID, instrumentName) {
    super(themeID, [makeViewInstanceID(instrumentName)], {
      instrumentName: instrumentName,
      viewInstanceID: makeViewInstanceID(instrumentName),
      labelTxtClassName: `${instrumentName}_value_label_txt`,
      pointerClassName: `${instrumentName}ValuePointer`
    });
    this.valueChangeAnimation = cascadingGet(this.theme,
      [`design.products.${this.instrumentName}.screen.animation.valuechange.start.label`,
        `design.default.screen.animation.valuechange.start.label`]);

    this.value_label_margin = 0;  // px
    this.value_label_topmargin = 0;  // em

    this.value = null;
    this.textOfClosestSegment = null;

    function makeViewInstanceID (instrumentName) {
      return `${instrumentName}ValueLabel`;
    }
  }

  build () {
    super.build();

    // Pre-flight check
    if (!this.valid) {
      return;
    }

    // Start build
    this.view = cloneInstance(this.themeID, this.viewInstanceID);

    // Add gap between indicator and value label, per theme setting. In horizontal layouts, the gap
    // is a left margin. In vertical layouts, the gap is a top margin.
    let viewGap = this.getThemeSetting(`design.products.${this.instrumentName}.screen.value_label.gap.horizontal`);
    let viewDirection = this.getThemeSetting(`design.products.${this.instrumentName}.screen.main.direction`, 'horizontal');
    if (notNil(viewGap)) {
      if (notNil(viewDirection) && viewDirection === 'vertical') {
        this.view.style.marginTop = `${viewGap}px`;
      } else {
        this.view.style.marginLeft = `${viewGap}px`;
      }
    }

    // The "valueLabel" <div> from the theme includes a text <div> in which to display the
    // current indicator value. For layout purposes, add a nested <div> to that text <div>.
    // The text in the inner <div> can then be top-aligned while the outer div is vertically
    // centered (with flex). Adding the inner <div> here, rather than in the theme, makes
    // the theme structure simpler for designers.
    this.value_label_txt = this.view.querySelector(`.${this.labelTxtClassName}`);
    this.value_label_txt.textContent = '';  // Clear out any author-time placeholder text
    this.value_label_txt.append(div(null, 'label_txt'));
    this.pointer = this.view.querySelector(`.${this.pointerClassName}`);
  }

  getSize () {
    let size = {width: 0, height: 0};
    size.width = this.value_label_txt.offsetWidth;
    size.height = this.value_label_txt.offsetHeight + (isNil(this.pointer) ? 0 : this.pointer.offsetHeight);

    return size;
  }

  setValue (percent) {
    percent = percent/100;
    let textOfClosestSegment;

    // If the value to be displayed on the label hasn't changed, don't update the value label
    // (these checks prevent the label from animating unnecessarily when the previous value
    // matches the new value).
    if (this.value === percent) {
      // New percentage matches old percentage, so don't set the label
      return;
    } else {
      this.value = percent;

      // Check if new "text of closest segment" matches old
      if (notNil(this.segmentLabels)) {
        textOfClosestSegment = this.getTextOfClosestSegment();

        if (this.textOfClosestSegment === textOfClosestSegment) {
          // New text of closest segment matches old, so don't set the label
          return;
        } else {
          // Store the new value (percent) and new text of the closest segment (e.g., "Strongly Agree")
          this.textOfClosestSegment = textOfClosestSegment;
        }
      }
    }

    // Set value label text
    let label_txt = this.value_label_txt.querySelector('.label_txt');
    if (notNil(this.segmentLabels)) {
      // Display the text of the closest segment label
      setText([label_txt], textOfClosestSegment);
      // Automatically reduce size of font to fit into value label (if necessary)
      label_txt.style.removeProperty('font-size');
      fitText(label_txt, this.value_label_txt.clientWidth-(this.value_label_margin*2), this.value_label_txt.clientHeight);
    } else {
      // No segment labels provided, so display the raw percentage (e.g., "80%")
      setText([label_txt], `${percent}%`);
    }
    if (notNil(this.valueChangeAnimation)) {
      play(label_txt, this.valueChangeAnimation);
    }
  }

  getTextOfClosestSegment () {
    let indexOfClosestSegment = Math.max(0, Math.round(this.value * this.segmentLabels.length) - 1);
    return this.segmentLabels[indexOfClosestSegment];
  }

  /**
   * Assigns a list of names for the segments of the indicator. In MegaScreen, the segment labels
   * are typically the text of the answers to a question.
   *
   * @param labels An array of strings representing segments on the survey bar.
   */
  setSegmentLabels (labels) {
    this.segmentLabels = labels;
    // If the segment labels change (e.g., on a Guess Gauge or Mood Meter), then refresh the label text.
    // This ensure that the label displays the correct value in the case where the initial percentage
    // is set to 0 BEFORE the segment labels are assigned, as can happen at the beginning of a scene.
    this.refreshLabel();
  }

  refreshLabel () {
    let oldValue = this.value;
    this.value = null;
    this.setValue(oldValue/100);
  }

  setValueLabelMargin (margin) {
    this.value_label_margin = margin;

    let label_txt = this.value_label_txt.querySelector('.label_txt');
    label_txt.style.marginLeft = `${margin}px`;
    label_txt.style.marginRight = `${margin}px`;
  }

  setValueLabelTopMargin (margin) {
    this.value_label_topmargin = margin;
    this.value_label_txt.querySelector('.label_txt').style.marginTop = `${margin}em`;
  }
}
